// ── IMPORTS: always first ───────────────────────────────────────────
import { TScene, TObstacle } from "../../../models/objects/objects";
import { TSprite } from "../../../models/objects/sprite";
import { CharacterConfig, SceneConfig, ObjectConfig } from "../types/config";

// Internal module namespaces used throughout the file
import * as _engineManager from "./engineManager";
import * as _mouvement from "./mouvement";
import * as _action from "./action";
import * as _misc from "./misc";
import * as _text from "./text";
import * as _sound from "./sound";
import * as _character from "./character";
import * as _scene from "./scene";
import * as _object from "./object";

// ── RE-EXPORTS (after all imports) ──────────────────────────────────
// Keep the same public API as before (namespaced re-exports)
export const engineManager = _engineManager;
export const mouvement = _mouvement;
export const action = _action;
export const misc = _misc;
export const text = _text;
export const sound = _sound;
export const character = _character;
export const scene = _scene;
export const object = _object;

// ─────────────────────────────────────────────────────────────
// Contexte
// ─────────────────────────────────────────────────────────────
type FunctionContext = {
  sprites: TSprite[];
  addCharacterCtx: (character: CharacterConfig) => void;
  syncCharactersCtx: (characters: CharacterConfig[]) => void;

  scenes: TScene[];
  updateSceneCtx: (updates: Partial<SceneConfig>) => void;

  obstacles: TObstacle[];
  addObjectCtx: (obj: ObjectConfig) => void;
  updateObjectCtx: (id: string, updates: Partial<ObjectConfig>) => void;
  removeObjectCtx: (id: string) => void;
};

let functionContext: FunctionContext = {
  sprites: [],
  addCharacterCtx: () => { },
  syncCharactersCtx: () => { },

  scenes: [],
  updateSceneCtx: () => { },

  obstacles: [],
  addObjectCtx: () => { },
  updateObjectCtx: () => { },
  removeObjectCtx: () => { },
};

export const initFunctions = (context: FunctionContext) => {
  functionContext = context;
};

export const getContext = (): FunctionContext => functionContext;

export const setEngine = _engineManager.setEngine;
export const getEngine = _engineManager.getEngine;

// ─────────────────────────────────────────────────────────────
// Helpers internes
// ─────────────────────────────────────────────────────────────

/** Aplati récursivement les fonctions trouvées dans les objets fournis. */
function flattenFunctions(...mods: any[]): Record<string, Function> {
  const flat: Record<string, Function> = {};
  const seen = new WeakSet<object>();
  const visit = (obj: any) => {
    if (!obj || typeof obj !== "object" || seen.has(obj)) return;
    seen.add(obj);
    for (const [k, v] of Object.entries(obj)) {
      if (typeof v === "function") {
        flat[k] = v as Function;
      } else if (v && typeof v === "object") {
        visit(v);
      }
    }
  };
  mods.forEach(visit);
  return flat;
}

/** Renvoie l'id du personnage par défaut (player, sinon le 1er). */
async function wait_for_default_character(maxWaitMs = 1000): Promise<string> {
  const EM = _engineManager as any;
  const start = Date.now();
  while (Date.now() - start < maxWaitMs) {
    try {
      const eng = EM.getEngine?.();
      const byPlayer = eng?.getCharacter?.("player")?.id;
      if (byPlayer) return String(byPlayer);
      const all = eng?.getAllCharacterIds?.() ?? [];
      if (all.length) return String(all[0]);
    } catch { }
    await new Promise((r) => setTimeout(r, 50));
  }
  return "";
}

const ANGLES = {
  RIGHT: 0,
  UP: 90,
  LEFT: 180,
  DOWN: 270,
  RU: 45, // right-up
  RD: 315, // right-down
  LU: 135, // left-up
  LD: 225, // left-down
} as const;

type Direction =
  | "RIGHT"
  | "LEFT"
  | "UP"
  | "DOWN"
  | "FORWARD"
  | "BACKWARD"
  | "RIGHT_UP"
  | "RIGHT_DOWN"
  | "LEFT_UP"
  | "LEFT_DOWN";

/** Tourner un personnage vers une direction (ou angle) normalisée. */
async function turn_to(id: string, dir?: Direction, angle?: number) {
  if (dir === "BACKWARD") return _mouvement.turnCharacter?.(id, "BACKWARD", 0);
  if (dir === "FORWARD") return _mouvement.turnCharacter?.(id, "", 0);

  const a =
    angle ??
    (dir === "RIGHT"
      ? ANGLES.RIGHT
      : dir === "LEFT"
        ? ANGLES.LEFT
        : dir === "UP"
          ? ANGLES.UP
          : dir === "DOWN"
            ? ANGLES.DOWN
            : dir === "RIGHT_UP"
              ? ANGLES.RU
              : dir === "RIGHT_DOWN"
                ? ANGLES.RD
                : dir === "LEFT_UP"
                  ? ANGLES.LU
                  : dir === "LEFT_DOWN"
                    ? ANGLES.LD
                    : 0);
  return _mouvement.turnCharacter?.(id, "", a);
}

/** Mouvement générique (walk/jump) par id avec direction/angle + distance. */
async function move_generic_by_id(
  mode: "walk" | "jump",
  id: string | number,
  opts?: { dir?: Direction; angle?: number; dist?: number }
) {
  const _id = String(id);
  const dist = Math.max(0, opts?.dist ?? 1);
  const dir = opts?.dir;
  const angle = opts?.angle;

  if (dir || typeof angle === "number") {
    await turn_to(_id, dir, angle);
  }

  if (mode === "walk") return _mouvement.walkCharacter?.(_id, dist);
  return _mouvement.jumpCharacter?.(_id, dist);
}

/** Mouvement générique (walk/jump) sur le personnage par défaut. */
async function move_generic_default(
  mode: "walk" | "jump",
  opts?: { dir?: Direction; angle?: number; dist?: number }
) {
  const id = await wait_for_default_character();
  if (!id) {
    console.warn("[move_generic_default] No character available");
    return;
  }
  return move_generic_by_id(mode, id, opts);
}

// ─────────────────────────────────────────────────────────────
// API HARMONISÉE (snake_case) + ALIAS camelCase / byId
// ─────────────────────────────────────────────────────────────

/** ── Actions / communication / util ───────────────────────── */
const api_core = {
  say: async (msg: any) => {
    const id = await wait_for_default_character();
    if (!id) return console.warn("[say] No character available");
    _action.sayCharacter?.(id, msg);
  },
  say_by_id: (id: string | number, msg: any) =>
    _action.sayCharacter?.(String(id), msg),

  activate_auto_pilot: async (id: string | number) =>
    _action.activateAutoPilot?.(String(id)),
  desactivate_auto_pilot: async (id: string | number) =>
    _action.desactivateAutoPilot?.(String(id)),

  set_win: () => _action.setWin?.(),
  is_picked: (id?: string | number) => _action.isPicked?.(id),
  set_gravity: (enable: boolean, gravityY?: number) =>
    _action.setGravity?.(enable, gravityY),
  move_to_default: async (x: number, y: number) => {
    const id = await wait_for_default_character();
    if (!id) return console.warn("[move_to_default] No character available");
    _action.move_to?.(id, x, y);
  },

  // find_character: (id: string | number) =>
  //   (_engineManager as any).getEngine?.()?.getCharacter(String(id)) ?? null,

  find_object: (id: string | number) => {
    const eng = (_engineManager as any).getEngine?.();
    return (
      eng?.getObject?.(String(id)) ??
      eng?.phaserScene?.getObjectById?.(String(id)) ??
      null
    );
  },

  // random helpers
  random: (min: number, max: number) => {
    if (typeof min !== "number" || typeof max !== "number")
      throw new Error("random(min,max) attend deux nombres");
    if (min > max) [min, max] = [max, min];
    return Math.floor(Math.random() * (max - min + 1)) + min;
  },
  random_item: <T,>(arr: T[]): T | undefined => {
    if (!Array.isArray(arr)) throw new Error("random_item attend un tableau");
    if (arr.length === 0) return undefined;
    return arr[Math.floor(Math.random() * arr.length)];
  },
};

/** ── Mouvements par défaut (sans id) ───────────────────────── */
/** ── Mouvements par défaut (sans id) ───────────────────────── */
const api_move_default = {
  // walk
  walk: (dist: number = 1) => move_generic_default("walk", { dist }),
  walk_forward: () => move_generic_default("walk", { dir: "FORWARD" }),
  walk_backward: () => move_generic_default("walk", { dir: "BACKWARD" }),
  walk_right: () => move_generic_default("walk", { dir: "RIGHT" }),
  walk_left: () => move_generic_default("walk", { dir: "LEFT" }),
  walk_up: () => move_generic_default("walk", { dir: "UP" }),
  walk_down: () => move_generic_default("walk", { dir: "DOWN" }),
  walk_right_up: () => move_generic_default("walk", { dir: "RIGHT_UP" }),
  walk_right_down: () => move_generic_default("walk", { dir: "RIGHT_DOWN" }),
  walk_left_up: () => move_generic_default("walk", { dir: "LEFT_UP" }),
  walk_left_down: () => move_generic_default("walk", { dir: "LEFT_DOWN" }),
  walk_choice: (dir: "FORWARD" | "BACKWARD" = "FORWARD") =>
    move_generic_default("walk", { dir }),

  // jump
  jump: (h: number = 1) => move_generic_default("jump", { dist: h }),
  jump_forward: () => move_generic_default("jump", { dir: "FORWARD" }),
  jump_backward: () => move_generic_default("jump", { dir: "BACKWARD" }),
  jump_right: () => move_generic_default("jump", { dir: "RIGHT" }),
  jump_left: () => move_generic_default("jump", { dir: "LEFT" }),
  jump_up: () => move_generic_default("jump", { dir: "UP" }),
  jump_down: () => move_generic_default("jump", { dir: "DOWN" }),
  jump_right_up: () => move_generic_default("jump", { dir: "RIGHT_UP" }),
  jump_right_down: () => move_generic_default("jump", { dir: "RIGHT_DOWN" }),
  jump_left_up: () => move_generic_default("jump", { dir: "LEFT_UP" }),
  jump_left_down: () => move_generic_default("jump", { dir: "LEFT_DOWN" }),
  jump_choice: (dir: "FORWARD" | "BACKWARD" = "FORWARD") =>
    move_generic_default("jump", { dir }),

  // turn (corrigé)
  turn: async (angle: number = 90) => {
    const id = await wait_for_default_character();
    if (!id) return console.warn("[turn] No character available");
    return _mouvement.turnCharacter?.(id, "", angle);
  },
  turn_right_or_left: async (dir: "RIGHT" | "LEFT" = "RIGHT") => {
    const id = await wait_for_default_character();
    if (!id) return console.warn("[turn_right_or_left] No character available");
    return _mouvement.turnCharacter?.(id, dir, 90);
  },
  choose_turn_angle: async (dir: "RIGHT" | "LEFT" = "RIGHT", angle = 90) => {
    const id = await wait_for_default_character();
    if (!id) return console.warn("[choose_turn_angle] No character available");
    return _mouvement.turnCharacter?.(id, dir, angle);
  },

  // variables (default)
  variable_walk: (dist: number) => move_generic_default("walk", { dist }),
  variable_jump: (h: number) => move_generic_default("jump", { dist: h }),
  variable_turn: async (angle: number) => {
    const id = await wait_for_default_character();
    if (!id) return console.warn("[variable_turn] No character available");
    return _mouvement.turnCharacter?.(id, "", angle);
  },
};


/** ── Mouvements par id ─────────────────────────────────────── */
const api_move_by_id = {
  // walk
  walk_by_id: (id: string | number, dist: number = 1) =>
    move_generic_by_id("walk", id, { dist }),
  walk_forward_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "FORWARD" }),
  walk_backward_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "BACKWARD" }),
  walk_right_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "RIGHT" }),
  walk_left_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "LEFT" }),
  walk_up_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "UP" }),
  walk_down_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "DOWN" }),
  walk_right_up_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "RIGHT_UP" }),
  walk_right_down_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "RIGHT_DOWN" }),
  walk_left_up_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "LEFT_UP" }),
  walk_left_down_by_id: (id: string | number) =>
    move_generic_by_id("walk", id, { dir: "LEFT_DOWN" }),
  walk_choice_by_id: (id: string | number, dir: "FORWARD" | "BACKWARD") =>
    move_generic_by_id("walk", id, { dir }),
  walk_choice_by_unit_by_id: (
    id: string | number,
    dir: "FORWARD" | "BACKWARD",
    dist: number
  ) => move_generic_by_id("walk", id, { dir, dist }),

  // jump
  jump_by_id: (id: string | number, h: number = 1) =>
    move_generic_by_id("jump", id, { dist: h }),
  jump_forward_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "FORWARD" }),
  jump_backward_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "BACKWARD" }),
  jump_right_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "RIGHT" }),
  jump_left_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "LEFT" }),
  jump_up_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "UP" }),
  jump_down_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "DOWN" }),
  jump_right_up_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "RIGHT_UP" }),
  jump_right_down_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "RIGHT_DOWN" }),
  jump_left_up_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "LEFT_UP" }),
  jump_left_down_by_id: (id: string | number) =>
    move_generic_by_id("jump", id, { dir: "LEFT_DOWN" }),
  jump_choice_by_id: (id: string | number, dir: "FORWARD" | "BACKWARD") =>
    move_generic_by_id("jump", id, { dir }),
  jump_choice_by_unit_by_id: (
    id: string | number,
    dir: "FORWARD" | "BACKWARD",
    dist: number
  ) => move_generic_by_id("jump", id, { dir, dist }),

  // turn
  turn_by_id: (id: string | number, angle: number = 90) =>
    move_generic_by_id("walk", id, { angle }),
  turn_right_or_left_by_id: (id: string | number, dir: "RIGHT" | "LEFT" = "RIGHT") =>
    move_generic_by_id("walk", id, { dir }),
  choose_turn_angle_by_id: (
    id: string | number,
    dir: "RIGHT" | "LEFT" = "RIGHT",
    angle = 90
  ) => move_generic_by_id("walk", id, { dir, angle }),

  // variables (by id)
  variable_walk_by_id: (id: string | number, d: number) =>
    move_generic_by_id("walk", id, { dist: d }),
  variable_jump_by_id: (id: string | number, h: number) =>
    move_generic_by_id("jump", id, { dist: h }),
  variable_turn_by_id: (id: string | number, a: number) =>
    move_generic_by_id("walk", id, { angle: a }),
};

/** ── Texte ─────────────────────────────────────────────────── */
const api_text = {
  create: (content: string, x: number, y: number) =>
    _text.createTextObject?.(content, x, y, "Arial", 16, "#ffffff", ""),
  create_full: (...args: any[]) => {
    const a = args.length === 8 ? args.slice(1) : args; // [content,x,y,font,size,color,bg]
    const [content, x, y, font, size, color, bg] = a;
    return _text.createTextObject?.(content, x, y, font, size, color, bg);
  },
  destroy: (id: string | number) => _text.destroyTextObject?.(id),
  move: (id: string | number, x: number, y: number) =>
    _text.moveTextObject?.(id, x, y),
  set_content: (id: string | number, s: string) =>
    _text.setTextContent?.(id, s),
  add_content: (id: string | number, s: string) =>
    _text.addTextContent?.(id, s),
  replace_content: (id: string | number, oldS: string, newS: string) =>
    _text.replaceTextContent?.(id, oldS, newS),
  cut: (id: string | number, start: number, end: number) =>
    _text.cutTextContent?.(id, start, end),
  reverse: (id: string | number) => _text.reverseTextContent?.(id),
  to_uppercase: (id: string | number) => _text.toUppercaseTextContent?.(id),
  to_lowercase: (id: string | number) => _text.toLowercaseTextContent?.(id),
  set_font: (id: string | number, font: string) => _text.setTextFont?.(id, font),
  set_font_size: (id: string | number, size: number) =>
    _text.setFontSize?.(id, size),
  set_color: (id: string | number, color: string) =>
    _text.setTextColor?.(id, color),
  set_background_color: (id: string | number, color: string) =>
    _text.setTextBackgroundColor?.(id, color),
  get_content: (id: string | number) => _text.getTextContentById?.(id),
  find_word_position: (id: string | number, w: string) =>
    _text.findTextWordPosition?.(id, w),
  replace_word: (id: string | number, oldW: string, newW: string) =>
    _text.replaceWordInText?.(id, oldW, newW),
  count_length: (id: string | number) => _text.countTextLength?.(id),
  count_char_occurrences: (id: string | number, ch: string) =>
    _text.countCharacterOccurrences?.(id, ch),
};

/** ── Sons ──────────────────────────────────────────────────── */
const api_sound = {
  play_sound: (name: string) => _sound.playSound?.(name),
  stop_sound: (name: string) => _sound.stopSound?.(name),
  // alias courts
  playSound: (name: string) => _sound.playSound?.(name),
  stopSound: (name: string) => _sound.stopSound?.(name),
};

/** ── Objets (exposition directe + helpers) ─────────────────── */
const api_object = {
  add_object: _object.addObject,
  add_object_with_size: _object.addObjectWithSize,
  add_pickable: _object.addPickable,
  add_pickable_with_size: _object.addPickableWithSize,
  add_decor: _object.addDecor,
  add_decor_with_size: _object.addDecorWithSize,
  add_immovable: _object.addImmovable,
  add_immovable_with_size: _object.addImmovableWithSize,
  add_pushable: _object.addPushable,
  add_pushable_with_size: _object.addPushableWithSize,

  destroy_object: _object.destroyObject,
  move_object: _object.moveObject,
  resize_object: _object.resizeObject,

  activate_pickable: (id: string | number) => _object.activatePickable(id),
  desactivate_pick_order: _object.desactivatePickOrder,
  activate_pick_order: _object.activatePickOrder,

  activate_immovable: (id: string | number) => _object.activateImmovable(id),
  desactivate_immovable: _object.desactivateImmovable,

  activate_pushable: (id: string | number) => _object.activatePushable(id),
  desactivate_pushable: _object.desactivatePushable,

  activate_decor: _object.activateDecor,
  desactivate_decor: _object.desactivateDecor,

  // alias historiques conservés
  addObject: _object.addObject,
  addObjectWithSize: _object.addObjectWithSize,
  addPickable: _object.addPickable,
  addPickableWithSize: _object.addPickableWithSize,
  addDecor: _object.addDecor,
  addDecorWithSize: _object.addDecorWithSize,
  addImmovable: _object.addImmovable,
  addImmovableWithSize: _object.addImmovableWithSize,
  addPushable: _object.addPushable,
  addPushableWithSize: _object.addPushableWithSize,
  destroyObject: _object.destroyObject,
  moveObject: _object.moveObject,
  resizeObject: _object.resizeObject,
  activatePickable: (id: string | number) => _object.activatePickable(id),
  activatePickOrder: _object.activatePickOrder,
  desactivatePickOrder: _object.desactivatePickOrder,
  activateImmovable: (id: string | number) => _object.activateImmovable(id),
  desactivateImmovable: _object.desactivateImmovable,
  activatePushable: (id: string | number) => _object.activatePushable(id),
  desactivatePushable: _object.desactivatePushable,
  activateDecor: _object.activateDecor,
  desactivateDecor: _object.desactivateDecor,
};

// ─────────────────────────────────────────────────────────────
// Alias de compatibilité (camelCase / byId) générés une fois
// ─────────────────────────────────────────────────────────────
function build_compat_aliases(): Record<string, Function> {
  const map: Record<string, Function> = {};

  // génère foo_byId → foo_by_id
  const aliasById = (srcName: string) => {
    const dstName = srcName.replace(/byId$/, "by_id");
    const fn =
      (api_move_by_id as any)[dstName] ??
      (api_move_by_id as any)[srcName] ??
      (api_move_default as any)[dstName] ??
      (api_move_default as any)[srcName];
    if (typeof fn === "function") map[srcName] = fn.bind(null);
  };

  // mouvements by_id -> camel byId
  const ids = Object.keys(api_move_by_id);
  for (const k of ids) {
    const camel = k.replace(/_by_id$/, "_byId");
    (map as any)[camel] = (api_move_by_id as any)[k];
  }

  // quelques noms courants explicitement demandés
  [
    "walk_byId",
    "walk_forward_byId",
    "walk_backward_byId",
    "walk_right_byId",
    "walk_left_byId",
    "walk_up_byId",
    "walk_down_byId",
    "walk_right_up_byId",
    "walk_right_down_byId",
    "walk_left_up_byId",
    "walk_left_down_byId",
    "walk_choice_byId",
    "walk_choice_by_unit_byId",
    "jump_byId",
    "jump_forward_byId",
    "jump_backward_byId",
    "jump_right_byId",
    "jump_left_byId",
    "jump_up_byId",
    "jump_down_byId",
    "jump_right_up_byId",
    "jump_right_down_byId",
    "jump_left_up_byId",
    "jump_left_down_byId",
    "jump_choice_byId",
    "jump_choice_by_unit_byId",
    "turn_byId",
    "turn_right_or_left_byId",
    "choose_turn_angle_byId",
    "variable_walk_byId",
    "variable_jump_byId",
    "variable_turn_byId",
  ].forEach(aliasById);

  return map;
}

// ── API identité pour le shim / Python ─────────────────────────
const api_identity = {
  // 1) Id par défaut (rapide, sans polling)
  async getDefaultCharacterId(): Promise<string> {
    const eng = (_engineManager as any).getEngine?.();
    if (!eng) return "";

    // essaye 'player' d'abord
    try {
      const p = eng.getCharacter?.("player");
      if (p?.id != null) return String(p.id);
    } catch { }

    // sinon, le 1er id de la liste
    try {
      const ids = eng.getAllCharacterIds?.() ?? [];
      if (Array.isArray(ids) && ids.length) return String(ids[0]);
    } catch { }

    return "";
  },

  // alias snake_case (le shim sait regarder plusieurs noms)
  get_default_character_id(): Promise<string> {
    return this.getDefaultCharacterId();
  },

  // 2) Liste d’ids des persos
  getAllCharacterIds(): Array<string | number> {
    const eng = (_engineManager as any).getEngine?.();
    // chemin “officiel” si dispo
    if (eng?.getAllCharacterIds) return eng.getAllCharacterIds();

    // fallbacks tolérants (au cas où le stagiaire…)
    const scene = eng?.phaserScene;
    const arr =
      scene?.getAllCharacterIds?.() ??
      scene?.characters?.map?.((c: any) => c?.id) ??
      [];
    return Array.isArray(arr) ? arr.filter((x: any) => x != null) : [];
  },

  // 3) Accès direct à un perso
  getCharacter(id: string | number) {
    const eng = (_engineManager as any).getEngine?.();
    return eng?.getCharacter?.(String(id)) ??
      eng?.phaserScene?.getCharacterById?.(String(id)) ??
      null;
  },
};

// ─────────────────────────────────────────────────────────────
// Construction de la map complète exposée à l’IDE
// ─────────────────────────────────────────────────────────────
export function getAllFunctionMap(withCompatAliases = true): Record<string, Function> {
  const flat = flattenFunctions(
    _engineManager,
    _mouvement,
    _action,
    _misc,
    _text,
    _sound,
    _character,
    _scene,
    _object,
  );

  // on retire set/getEngine du global direct
  const filtered = { ...flat } as Record<string, Function>;
  delete (filtered as any).setEngine;
  delete (filtered as any).getEngine;

  const map: Record<string, Function> = {
    ...filtered,
    ...api_core,
    ...api_move_default,
    ...api_move_by_id,
    ...api_text,
    ...api_sound,
    ...api_object,
    ...api_identity,
  };

  return withCompatAliases ? { ...map, ...build_compat_aliases() } : map;
}


/**
 * Expose toutes les fonctions pour l'IDE :
 * - window.__IDE_GLOBALS__ = { ... }
 * - (optionnel) copie aussi sur window.<fn> pour compat totale
 */
export function exposeIDEGlobals(options?: { alsoOnWindow?: boolean; key?: string }) {
  const { alsoOnWindow = true, key = "__IDE_GLOBALS__" } = options || {};
  const map = getAllFunctionMap(true);
  (window as any)[key] = map;
  if (alsoOnWindow) Object.assign(window as any, map);
  // console.log("[functions.index] exposeIDEGlobals keys:", Object.keys(map).sort());
}

/** Nettoie l'exposition globale. */
export function hideIDEGlobals(options?: { alsoFromWindow?: boolean; key?: string }) {
  const { alsoFromWindow = true, key = "__IDE_GLOBALS__" } = options || {};
  const current = (window as any)[key] as Record<string, any> | undefined;
  if (current && alsoFromWindow) {
    Object.keys(current).forEach((k) => {
      try {
        delete (window as any)[k];
      } catch { }
    });
  }
  try {
    delete (window as any)[key];
  } catch { }
}

